import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { getValidAccessToken } from '../utils/tokenManager';
import {
  isPushSupported,
  isIos,
  isIosPwa,
  requestPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getSubscriptionStatus,
} from '../utils/pushNotificationManager';
import closeIcon from '../assets/icons/close.svg';
import lockIcon from '../assets/icons/lock.svg';

/**
 * AccountSettings component allows users to view their account info,
 * change their password, and manage push notification preferences
 */
export function AccountSettings() {
  const { user, apiCall } = useOutletContext();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordMessage, setPasswordMessage] = useState(null);

  // Push notification state
  const [pushStatus, setPushStatus] = useState({ supported: false, subscribed: false, permission: 'default' });
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(false);

  const isAgent = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'auditor';

  // Fetch user info when component mounts
  useEffect(() => {
    const fetchUserInfo = async () => {
      setLoading(true);
      try {
        const response = await apiCall(`${API_BASE_URL}/auth/me`);
        if (response.success && response.user) {
          // Transform camelCase API response to match frontend format
          const transformedUser = {
            id: response.user.id,
            userId: response.user.userId,
            email: response.user.email,
            role: response.user.role,
            store_id: response.user.storeId,
            name: response.user.name,
            first_name: response.user.firstName,
            last_name: response.user.lastName,
            phone_number: response.user.phoneNumber,
            driver_license_number: response.user.driverLicenseNumber,
            driver_license_state: response.user.driverLicenseState,
            created_at: response.user.createdAt,
          };
          setUserInfo(transformedUser);
        } else {
          // Fallback to user from context if API call fails
          setUserInfo(user);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        // Fallback to user from context
        setUserInfo(user);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [apiCall, user]);

  // Check push status and load preferences for agents
  const refreshPushStatus = useCallback(async () => {
    if (!isAgent) return;
    const status = await getSubscriptionStatus();
    setPushStatus(status);
  }, [isAgent]);

  useEffect(() => {
    refreshPushStatus();
  }, [refreshPushStatus]);

  useEffect(() => {
    if (!isAgent) return;
    const loadPrefs = async () => {
      try {
        const response = await apiCall(`${API_BASE_URL}/agent/preferences`);
        if (response.success) {
          setNotifPrefs(response.data);
        }
      } catch (e) {
        console.error('Error loading notification preferences:', e);
      }
    };
    loadPrefs();
  }, [apiCall, isAgent]);

  const handleEnablePush = async () => {
    setPushLoading(true);
    setPushMessage(null);
    try {
      const permission = await requestPushPermission();
      if (permission === 'denied') {
        setPushMessage({ type: 'warning', text: 'Notification permission was denied. Please enable notifications in your browser settings and try again.' });
        await refreshPushStatus();
        return;
      }
      if (permission === 'granted') {
        await subscribeToPush(apiCall);
        setPushMessage({ type: 'success', text: 'Push notifications enabled!' });
      }
      await refreshPushStatus();
    } catch (error) {
      setPushMessage({ type: 'danger', text: error.message || 'Failed to enable push notifications' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    setPushMessage(null);
    try {
      await unsubscribeFromPush(apiCall);
      setPushMessage({ type: 'info', text: 'Push notifications disabled.' });
      await refreshPushStatus();
    } catch (error) {
      setPushMessage({ type: 'danger', text: error.message || 'Failed to disable push notifications' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleSavePrefs = async () => {
    if (!notifPrefs) return;
    setPrefsLoading(true);
    try {
      const response = await apiCall(`${API_BASE_URL}/agent/preferences`, {
        method: 'PUT',
        body: JSON.stringify(notifPrefs),
      });
      if (response.success) {
        setPushMessage({ type: 'success', text: 'Notification preferences saved.' });
        setTimeout(() => setPushMessage(null), 3000);
      }
    } catch (error) {
      setPushMessage({ type: 'danger', text: 'Failed to save preferences.' });
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    // Validate passwords match
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    // Validate password requirements (Cognito requirements)
    if (changePasswordData.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=\[\]{};':"\\|,.<>\/])/;
    if (!passwordRegex.test(changePasswordData.newPassword)) {
      setPasswordMessage({
        type: 'error',
        text: 'Password must contain uppercase, lowercase, number, and special character'
      });
      return;
    }

    try {
      // Get the access token (required for Cognito changePassword API)
      const accessToken = await getValidAccessToken();

      if (!accessToken) {
        setPasswordMessage({ type: 'error', text: 'Authentication required. Please log in again.' });
        return;
      }

      // Call the backend with access token
      const response = await fetch(
        `${API_BASE_URL}/auth/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            oldPassword: changePasswordData.oldPassword,
            newPassword: changePasswordData.newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setChangePasswordData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setTimeout(() => {
          setShowChangePasswordForm(false);
          setPasswordMessage(null);
        }, 2000);
      } else {
        setPasswordMessage({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error.message || 'Error changing password' });
    }
  };

  const hourOptions = Array.from({ length: 24 }, (_, i) => (
    <option key={i} value={i}>
      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
    </option>
  ));

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Account Settings</h2>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>
          <h3>Your Information</h3>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2 text-muted">Loading your information...</p>
            </div>
          ) : (
            <Row>
              <Col md={6} className="mb-3">
                <div>
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Name</strong>
                  <span>
                    {userInfo?.role === 'admin' || userInfo?.role === 'manager' || userInfo?.role === 'auditor'
                      ? userInfo?.name || 'N/A'
                      : `${userInfo?.first_name || 'N/A'} ${userInfo?.last_name || ''}`}
                  </span>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div>
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Email</strong>
                  <span>{userInfo?.email || 'N/A'}</span>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div>
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Role</strong>
                  <span>{userInfo?.role ? userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1) : 'N/A'}</span>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <div>
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>User ID</strong>
                  <span>{userInfo?.id || 'N/A'}</span>
                </div>
              </Col>
              {(userInfo?.role === 'manager' || userInfo?.role === 'admin' || userInfo?.role === 'auditor') && userInfo?.store_id && (
                <Col md={6} className="mb-3">
                  <div>
                    <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Store ID</strong>
                    <span>{userInfo.store_id}</span>
                  </div>
                </Col>
              )}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Push Notifications Section — agents only */}
      {isAgent && (
        <Card className="mb-3">
          <Card.Header>
            <h3>Push Notifications</h3>
          </Card.Header>
          <Card.Body>
            {pushMessage && (
              <Alert variant={pushMessage.type} dismissible onClose={() => setPushMessage(null)}>
                {pushMessage.text}
              </Alert>
            )}

            {/* iOS Home Screen prompt */}
            {isIos() && !isIosPwa() && (
              <Alert variant="info" className="mb-3">
                <strong>iOS Users:</strong> To receive push notifications, you must add this app to your Home Screen.
                Tap the Share button in Safari, then select "Add to Home Screen".
              </Alert>
            )}

            {!pushStatus.supported ? (
              <Alert variant="warning">
                Push notifications are not supported in this browser. Please use Chrome, Firefox, Edge, or Safari 16.4+.
              </Alert>
            ) : (
              <>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div>
                    <strong>Status: </strong>
                    {pushStatus.subscribed ? (
                      <Badge bg="success">Enabled</Badge>
                    ) : pushStatus.permission === 'denied' ? (
                      <Badge bg="danger">Blocked</Badge>
                    ) : (
                      <Badge bg="secondary">Disabled</Badge>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-2 mb-3">
                  {!pushStatus.subscribed ? (
                    <Button
                      variant="primary"
                      onClick={handleEnablePush}
                      disabled={pushLoading}
                    >
                      {pushLoading ? <Spinner size="sm" className="me-2" /> : null}
                      {pushStatus.permission === 'denied' ? 'Re-request Permission' : 'Enable Push Notifications'}
                    </Button>
                  ) : (
                    <Button
                      variant="outline-danger"
                      onClick={handleDisablePush}
                      disabled={pushLoading}
                    >
                      {pushLoading ? <Spinner size="sm" className="me-2" /> : null}
                      Disable Push Notifications
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Notification Preferences */}
            {notifPrefs && (
              <>
                <hr />
                <h5 className="mb-3">Notification Preferences</h5>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="notify-inbound-sms"
                        label="Inbound SMS notifications"
                        checked={notifPrefs.notify_inbound_sms}
                        onChange={(e) => setNotifPrefs({ ...notifPrefs, notify_inbound_sms: e.target.checked })}
                        className="mb-2"
                      />
                      <Form.Check
                        type="switch"
                        id="notify-task-assigned"
                        label="Task assignment notifications"
                        checked={notifPrefs.notify_task_assigned}
                        onChange={(e) => setNotifPrefs({ ...notifPrefs, notify_task_assigned: e.target.checked })}
                        className="mb-2"
                      />
                      <Form.Check
                        type="switch"
                        id="enable-sms-failover"
                        label="SMS failover (text me if I don't respond)"
                        checked={notifPrefs.enable_sms_failover}
                        onChange={(e) => setNotifPrefs({ ...notifPrefs, enable_sms_failover: e.target.checked })}
                        className="mb-3"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem' }}>Do Not Disturb Start</Form.Label>
                        <Form.Select
                          value={notifPrefs.dnd_start_hour ?? ''}
                          onChange={(e) => setNotifPrefs({
                            ...notifPrefs,
                            dnd_start_hour: e.target.value === '' ? null : parseInt(e.target.value),
                          })}
                          size="sm"
                        >
                          <option value="">Use system default</option>
                          {hourOptions}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '0.85rem' }}>Do Not Disturb End</Form.Label>
                        <Form.Select
                          value={notifPrefs.dnd_end_hour ?? ''}
                          onChange={(e) => setNotifPrefs({
                            ...notifPrefs,
                            dnd_end_hour: e.target.value === '' ? null : parseInt(e.target.value),
                          })}
                          size="sm"
                        >
                          <option value="">Use system default</option>
                          {hourOptions}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button
                    variant="primary"
                    onClick={handleSavePrefs}
                    disabled={prefsLoading}
                    size="sm"
                  >
                    {prefsLoading ? <Spinner size="sm" className="me-2" /> : null}
                    Save Preferences
                  </Button>
                </Form>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      <Button
        variant="primary"
        onClick={() => setShowChangePasswordForm(!showChangePasswordForm)}
      >
        {showChangePasswordForm ? <><img src={closeIcon} alt="" className="icon-btn" />Cancel</> : <><img src={lockIcon} alt="" className="icon-btn" />Change Password</>}
      </Button>

      {showChangePasswordForm && (
        <Card className="mt-3">
          <Card.Header>
            <h3>Change Password</h3>
          </Card.Header>
          <Card.Body>
            {passwordMessage && (
              <Alert variant={passwordMessage.type === 'error' ? 'danger' : 'success'}>
                {passwordMessage.text}
              </Alert>
            )}
            <Form onSubmit={handleChangePassword}>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <Form.Control
                  type="password"
                  value={changePasswordData.oldPassword}
                  onChange={(e) => setChangePasswordData(prev => ({
                    ...prev,
                    oldPassword: e.target.value,
                  }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={changePasswordData.newPassword}
                  onChange={(e) => setChangePasswordData(prev => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))}
                  required
                />
                <Form.Text className="text-muted">
                  Password must be at least 8 characters and contain uppercase, lowercase, number, and special character.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={changePasswordData.confirmPassword}
                  onChange={(e) => setChangePasswordData(prev => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))}
                  required
                />
              </Form.Group>

              <Button type="submit" variant="primary">Change Password</Button>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default AccountSettings;
